import { Spinner } from '@chakra-ui/react';
import { useState } from 'react';
import { PageContainer } from '../../components/Layout/PageContainer';
import { ConstrainedProgramsTable } from '../../components/Tables/ConstrainedProgramsTable';
import { PageBreadcrumb } from '../../components/UI/PageBreadcrumb';
import { useProgramsConstrainedQuery } from '../../query/useConstrainedProgramsQuery';
import ProgramEditDialog from '../../components/ProgramEditDialog';
import { useToastContext } from '../../components/UI/ToastManager';
import { graphqlRequest } from '../../utils/graphqlRequest';
import { deleteProgramConstrainedMutation, createProgramConstrainedMutation, programConstrainedByIdQuery } from '../../constants/queries';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmationModal } from '../../components/UI/ConfirmationModal';

export function ConstrainedProgramsPage() {
  const { data, isLoading } = useProgramsConstrainedQuery();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<any>(null);
  const { showSuccess, showError } = useToastContext();
  const queryClient = useQueryClient();

  const handleEdit = (row: any) => {
    setSelectedProgram(row);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    console.log('Add new constrained program clicked');
    setSelectedProgram(null);
    setDialogOpen(true);
  };

  const handleDelete = (row: any) => {
    if (!row.id) {
      showError('No program ID found');
      return;
    }

    setProgramToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!programToDelete?.id) {
      showError('No program ID found');
      return;
    }

    try {
      await graphqlRequest(deleteProgramConstrainedMutation, { id: programToDelete.id }, showError);
      showSuccess('Program deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['constrainedPrograms'] });
    } catch (error) {
      console.error('Error deleting program:', error);
      showError('Failed to delete program');
    }
  };

  const handleCopy = async (row: any) => {
    if (!row.id) {
      showError('No program ID found');
      return;
    }

    try {
      const response = await graphqlRequest(programConstrainedByIdQuery, { id: row.id }, showError);
      const programData = response.programConstrained;
      
      if (!programData) {
        showError('Failed to fetch program data for copying');
        return;
      }

      const baseName = programData.name || programData.displayName?.[0]?.text || 'Copy';
      let copyName = `${baseName} Copy`;
      let copyNumber = 1;
      
      const existingData = data || [];
      const checkExistingName = (name: string) => {
        return existingData.some((item: any) => 
          (item.name === name) || 
          (item.displayName?.text === name)
        );
      };
      
      while (checkExistingName(copyName)) {
        copyNumber++;
        copyName = `${baseName} Copy ${copyNumber}`;
      }

      const copyData = JSON.parse(JSON.stringify(programData));
      
      copyData.name = copyName;
      if (copyData.displayName && copyData.displayName.length > 0) {
        copyData.displayName[0].text = copyName;
      } else {
        copyData.displayName = [
          {
            language: 'en',
            text: copyName
          }
        ];
      }

      if (copyData.steps) {
        copyData.steps.forEach((step: any) => {
          if (step.options) {
            step.options.forEach((option: any) => {
              if (option.exercises) {
                option.exercises.forEach((exerciseGroup: any) => {
                  if (exerciseGroup.options) {
                    exerciseGroup.options.forEach((exerciseOption: any) => {
                      if (exerciseOption.exercise && exerciseOption.exercise.id) {
                        exerciseOption.exerciseId = exerciseOption.exercise.id;
                        delete exerciseOption.exercise;
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }

      delete copyData.id;
      delete copyData.lastUpdateDate;

      await graphqlRequest(createProgramConstrainedMutation, { programConstrained: copyData }, showError);
      showSuccess('Program copied successfully');
      queryClient.invalidateQueries({ queryKey: ['constrainedPrograms'] });
    } catch (error) {
      console.error('Error copying program:', error);
      showError('Failed to copy program');
    }
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setSelectedProgram(null);
    }
  };

  return (
    <PageContainer>
      <PageBreadcrumb items={['Home', 'Program Constrained']} />
      {isLoading ? (
        <Spinner size="xl" mt="40px" color="teal.500" />
      ) : (
        <ConstrainedProgramsTable 
          data={data ?? []} 
          onEdit={handleEdit} 
          onAdd={handleAdd}
          onDelete={handleDelete}
          onCopy={handleCopy}
        />
      )}
      <ProgramEditDialog 
        open={isDialogOpen} 
        onOpenChange={handleDialogClose}
        programData={selectedProgram}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProgramToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Program"
        message={`Are you sure you want to delete "${programToDelete?.name || 'this program'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColorScheme="red"
      />
    </PageContainer>
  );
}